import {
  DestroyRef,
  Injectable,
  computed,
  inject,
  signal,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { Observable, Subscription, catchError, map, of } from 'rxjs';
import {
  AUMSourceEnum,
  CurrencyStringEnum,
  PublicReturnFeeEnum,
  PublicReturnGeneralClassificationEnum,
  PublicReturnSourceEnum,
} from '@aksia/enums';
import { TOASTER_SERVICE_TOKEN, UTILS } from '@aksia/infrastructure';
import {
  AUMPeriodicStream,
  IPeriodicStream,
  IPeriodicStreamPoint,
  PublicReturnPeriodicStream,
  TPeriodicStream,
  TPeriodicStreamRequest,
} from '@aksia/models';
import {
  PeriodicsProfileService,
  TPeriodicsEntityRef,
  periodicsEntityKey,
} from './periodics-profile.service';
import { PeriodicStreamService } from './periodic-stream.service';
import {
  TGridStream,
  TGridStreamPoint,
  TStreamPointChange,
  TStreamPointSnapshot,
  TStreamSelectionCell,
  TStreamStartDates,
  applyFieldToPoints,
  applyStreamPointValueChange,
  buildStreamUpdateRequest,
  fillPeriodicStream,
  historicalStreamRequest,
  initStreamSelection,
  isPointDirty,
  isPublicReturnsStream,
  isSavableStreamPoint,
  markedSelectionChanges,
  periodicsDisplayName,
  pointSnapshot,
  streamPointErrorMessage,
  streamPointErrors,
  streamStartDateFor,
  toPeriodicStreamModel,
  toYtdModels,
} from './periodic-stream.grid';
import { toStoreModelDTO } from '../../infrastructure/model/store-model.mapper';

//#region Types

export type TPeriodicsLoad = {
  key: string;
  streams: Array<TGridStream>;
  snapshots: Map<number, TStreamPointSnapshot>;
};

export type TPeriodicsDirtyStream = {
  stream: TGridStream;
  points: Array<TGridStreamPoint>;
};

export type TPeriodicsInvalidPoint = {
  stream: TGridStream;
  point: TGridStreamPoint;
  message: string;
};

//#endregion

@Injectable()
export class PeriodicsState {
  readonly #profileSvc = inject(PeriodicsProfileService);
  readonly #streamSvc = inject(PeriodicStreamService);
  readonly #toasterSvc = inject(TOASTER_SERVICE_TOKEN, { optional: true });
  readonly #ytdSubscriptions = new Map<number, Subscription>();

  readonly entityRef = signal<TPeriodicsEntityRef | undefined>(undefined);
  readonly revision = signal(0);
  readonly saving = signal(false);

  //#region Resources

  readonly profileResource = rxResource({
    params: () => this.entityRef(),
    stream: ({ params }) => this.#profileSvc.profile(params),
  });

  readonly profile = computed(() => {
    const ref = this.entityRef();
    const profile = this.profileResource.hasValue()
      ? this.profileResource.value()
      : undefined;
    return ref && profile?.key === periodicsEntityKey(ref)
      ? profile
      : undefined;
  });

  readonly requestedStreamTypes = computed(() => {
    const ref = this.entityRef();
    const profile = this.profile();
    if (!ref || !profile) return [];
    return profile.streamTypes.filter(
      (streamType) => !ref.streamTypes || ref.streamTypes.includes(streamType),
    );
  });

  readonly unavailableStreamTypes = computed(() => {
    const ref = this.entityRef();
    const profile = this.profile();
    if (!ref?.streamTypes || !profile) return [];
    return ref.streamTypes.filter(
      (streamType) => !profile.streamTypes.includes(streamType),
    );
  });

  readonly #loadKey = computed(() => {
    const profile = this.profile();
    return profile
      ? `${profile.key}|${this.requestedStreamTypes().join(',')}`
      : undefined;
  });

  readonly streamsResource = rxResource({
    params: () => {
      const ref = this.entityRef();
      const profile = this.profile();
      const key = this.#loadKey();
      if (!ref || !profile || !key || profile.missingStartDate) {
        return undefined;
      }
      return {
        key,
        startDates: profile.startDates,
        requests: this.requestedStreamTypes().map((streamType) =>
          historicalStreamRequest(ref.entityId, ref.entityTypeId, streamType),
        ),
      };
    },
    stream: ({ params }) =>
      this.#loadStreams(params.key, params.requests, params.startDates),
  });

  //#endregion

  //#region Computed

  readonly isLoading = computed(
    () => this.profileResource.isLoading() || this.streamsResource.isLoading(),
  );

  readonly error = computed(
    () => this.profileResource.error() ?? this.streamsResource.error(),
  );

  readonly #load = computed(() => {
    const key = this.#loadKey();
    const load = this.streamsResource.hasValue()
      ? this.streamsResource.value()
      : undefined;
    return key && load?.key === key ? load : undefined;
  });

  readonly streams = computed(() => this.#load()?.streams ?? []);

  readonly dirtyStreams = computed<Array<TPeriodicsDirtyStream>>(() => {
    this.revision();
    const snapshots = this.#load()?.snapshots;
    if (!snapshots) return [];
    return this.streams()
      .map((stream) => ({
        stream,
        points:
          stream.datapoints?.filter((point) =>
            isPointDirty(point, snapshots.get(point.$uid!)),
          ) ?? [],
      }))
      .filter(({ points }) => points.length > 0);
  });

  readonly invalidPoints = computed<Array<TPeriodicsInvalidPoint>>(() =>
    this.dirtyStreams().flatMap(({ stream, points }) =>
      points.flatMap((point) => {
        const message = streamPointErrorMessage(
          stream.streamType,
          point,
          streamPointErrors(stream.streamType, point),
        );
        return message ? [{ stream, point, message }] : [];
      }),
    ),
  );

  readonly displayName = computed(() => {
    const streamTypes = this.entityRef()?.streamTypes;
    return streamTypes?.length === 1
      ? periodicsDisplayName(streamTypes[0])
      : 'Periodics';
  });

  readonly isDirty = computed(() =>
    this.dirtyStreams().some(({ points }) =>
      points.some((point) => isSavableStreamPoint(point)),
    ),
  );

  //#endregion

  constructor() {
    inject(DestroyRef).onDestroy(() =>
      this.#ytdSubscriptions.forEach((subscription) =>
        subscription.unsubscribe(),
      ),
    );
  }

  //#region Edits

  onValueChange(
    stream: TGridStream,
    change: TStreamPointChange,
    forceDelete = false,
  ) {
    const snapshots = this.#load()?.snapshots;
    applyStreamPointValueChange(
      stream,
      change.dataItem,
      (point) => !UTILS.OBJECT.isNil(snapshots?.get(point.$uid!)?.value),
      forceDelete,
    );

    if (isPublicReturnsStream(stream.streamType)) {
      this.#updateYtd(stream as PublicReturnPeriodicStream);
    }
    this.#touch();
  }

  setMarkedPoints(
    stream: TGridStream,
    cells: Array<TStreamSelectionCell> | undefined,
  ) {
    Object.assign(stream, markedSelectionChanges(cells));
    this.#touch();
  }

  setSelectedSource(
    stream: TGridStream,
    source: PublicReturnSourceEnum | AUMSourceEnum | undefined,
  ): Array<TGridStreamPoint> {
    stream.selectedSource = source;
    return this.#applyField(
      stream.markedDataPoints ?? [stream.selectedDataPoint],
      'source',
      source,
    );
  }

  setSelectedFeeType(
    stream: PublicReturnPeriodicStream,
    feeType: PublicReturnFeeEnum | undefined,
  ): Array<TGridStreamPoint> {
    stream.selectedFeeType = feeType;
    return this.#applyField(stream.markedDataPoints, 'feeType', feeType);
  }

  setSelectedClassification(
    stream: PublicReturnPeriodicStream,
    classification: PublicReturnGeneralClassificationEnum | undefined,
  ): Array<TGridStreamPoint> {
    stream.selectedClassification = classification;
    return this.#applyField(
      stream.markedDataPoints,
      'classification',
      classification,
    );
  }

  setSelectedEstimationAsOf(
    stream: PublicReturnPeriodicStream,
    estimationAsOf: Date | undefined,
  ): Array<TGridStreamPoint> {
    stream.selectedEstimationAsOf = estimationAsOf;
    return this.#applyField(
      stream.markedDataPoints,
      'estimationAsOf',
      estimationAsOf,
    );
  }

  setSelectedCurrency(
    stream: AUMPeriodicStream,
    currency: CurrencyStringEnum | undefined,
  ): Array<TGridStreamPoint> {
    stream.selectedCurrency = currency;
    return this.#applyField(stream.datapoints, 'currency', currency);
  }

  //#endregion

  //#region Load & Save

  streamOf(streamType: TPeriodicStream): TGridStream | undefined {
    return this.streams().find((stream) => stream.streamType === streamType);
  }

  reload() {
    if (this.profileResource.error()) {
      this.profileResource.reload();
    } else {
      this.streamsResource.reload();
    }
  }

  save(): Observable<boolean> {
    if (!this.isDirty() || this.saving()) return of(false);

    const invalid = this.invalidPoints();
    if (invalid.length > 0) {
      this.#toasterSvc?.error(
        `Cannot save ${this.displayName()} due to validation errors: ${invalid
          .map(({ message }) => message)
          .join(' ')}`,
      );
      return of(false);
    }

    const profile = this.profile();
    const requests = this.dirtyStreams()
      .map(({ stream, points }) => ({
        stream,
        points: points.filter((point) => isSavableStreamPoint(point)),
      }))
      .filter(({ points }) => points.length > 0)
      .map(({ stream, points }) =>
        buildStreamUpdateRequest(stream, points, profile?.investmentProgramId),
      );

    if (
      requests.some(
        (request) =>
          isPublicReturnsStream(request.streamType) &&
          !profile?.investmentProgramId,
      )
    ) {
      this.#toasterSvc?.error(
        'Cannot save Public Returns: the fund has no investment program.',
      );
      return of(false);
    }

    this.saving.set(true);
    return this.#streamSvc.updateStreams(requests).pipe(
      map(() => {
        this.saving.set(false);
        this.#toasterSvc?.success(`${this.displayName()} saved successfully!`);
        this.reload();
        return true;
      }),
      catchError((error) => {
        this.saving.set(false);
        this.#toasterSvc?.error(
          `Failed to save ${this.displayName()}. ${error?.message ?? ''}`.trim(),
        );
        return of(false);
      }),
    );
  }

  //#endregion

  //#region Private

  #touch() {
    this.revision.update((revision) => revision + 1);
  }

  #applyField(
    points: Array<TGridStreamPoint | undefined> | undefined,
    field:
      'source' | 'feeType' | 'classification' | 'estimationAsOf' | 'currency',
    value: unknown,
  ) {
    const changed = applyFieldToPoints(points, field, value);
    this.#touch();
    return changed;
  }

  #updateYtd(stream: PublicReturnPeriodicStream) {
    this.#ytdSubscriptions.get(stream.$uid!)?.unsubscribe();
    this.#ytdSubscriptions.set(
      stream.$uid!,
      this.#streamSvc
        .getYTDCalculation(
          stream.entityId!,
          stream.entityTypeId!,
          stream.streamType!,
          toStoreModelDTO(
            stream.datapoints ?? [],
          ) as Array<IPeriodicStreamPoint>,
        )
        .subscribe((ytd: Array<IPeriodicStreamPoint>) => {
          stream.ytd = toYtdModels(ytd ?? []);
          this.#touch();
        }),
    );
  }

  #loadStreams(
    key: string,
    requests: Array<TPeriodicStreamRequest>,
    startDates: TStreamStartDates,
  ): Observable<TPeriodicsLoad> {
    if (requests.length === 0) {
      return of({ key, streams: [], snapshots: new Map() });
    }
    return this.#streamSvc.getStreams(requests).pipe(
      map((dtos: Array<IPeriodicStream> | undefined) => {
        const streams = (dtos ?? []).map((dto) =>
          initStreamSelection(
            fillPeriodicStream(
              toPeriodicStreamModel(dto),
              streamStartDateFor(dto.streamType!, startDates),
            ),
          ),
        );
        const snapshots = new Map<number, TStreamPointSnapshot>();
        streams.forEach((stream) =>
          stream.datapoints?.forEach((point) =>
            snapshots.set(point.$uid!, pointSnapshot(point)),
          ),
        );
        return { key, streams, snapshots };
      }),
    );
  }

  //#endregion
}
